import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Heart, Users, Music, Trophy, Shield, Star, ArrowRight, CheckCircle, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';



export default function FounderPage() {
  const [founderMessage, setFounderMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFounderMessage();
  }, []);

  const fetchFounderMessage = async () => {
    try {
      const response = await fetch(`${API}/platform/founder-message`);
      if (response.ok) {
        setFounderMessage(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch founder message:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="founder-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge className="mb-4 bg-primary/20 text-primary">
            <Heart className="w-3 h-3 mr-1" />
            Our Story
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-4">
            {founderMessage?.title || 'Why DMV Soundstage Exists'}
          </h1>
        </motion.div>

        {/* Mission Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass-card border-primary/30 p-6 text-center h-full">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Music className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">Artist-First</h3>
              <p className="text-sm text-muted-foreground">
                We don't take money from streams. Artists keep the value of their music.
              </p>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card border-secondary/30 p-6 text-center h-full">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">Fair Charts</h3>
              <p className="text-sm text-muted-foreground">
                Rankings powered by real requests. You can't buy your way to the top.
              </p>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass-card border-accent/30 p-6 text-center h-full">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">Community</h3>
              <p className="text-sm text-muted-foreground">
                Built for DC, Maryland & Virginia. Celebrating our region's sound.
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Founder Message Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass-card border-border p-8 md:p-12">
            <div className="flex justify-center mb-6">
              <Quote className="w-12 h-12 text-primary/30" />
            </div>
            
            <div className="prose prose-invert max-w-none">
              {founderMessage?.content?.split('\n\n').map((paragraph, index) => {
                // Check if it's a header (starts with **)
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <h3 key={index} className="font-heading text-xl font-bold text-foreground mt-8 mb-4">
                      {paragraph.replace(/\*\*/g, '')}
                    </h3>
                  );
                }
                // Check if it's a bold header within the text
                if (paragraph.includes('**')) {
                  const parts = paragraph.split('**');
                  return (
                    <p key={index} className="text-muted-foreground mb-4 leading-relaxed">
                      {parts.map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                      )}
                    </p>
                  );
                }
                // Check if it's a signature
                if (paragraph.startsWith('—')) {
                  return (
                    <p key={index} className="text-right text-secondary font-heading font-bold mt-8">
                      {paragraph}
                    </p>
                  );
                }
                return (
                  <p key={index} className="text-muted-foreground mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                );
              })}
            </div>
          </Card>
        </motion.div>

        {/* Trust Signals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="glass-card border-green-500/20 bg-green-500/5 p-6">
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              Our Commitments
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">No stream fees</p>
                  <p className="text-sm text-muted-foreground">We never take money from your plays</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Transparent fees</p>
                  <p className="text-sm text-muted-foreground">All costs shown before you pay</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">No pay-to-win</p>
                  <p className="text-sm text-muted-foreground">Chart positions can't be bought</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Real engagement</p>
                  <p className="text-sm text-muted-foreground">Rankings reflect actual fan support</p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground mb-6">
            Ready to join the movement?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button className="bg-secondary hover:bg-secondary/90" onClick={() => window.location.href = '/pricing'}>
              <Star className="w-4 h-4 mr-2" />
              Become an Artist
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/home'}>
              Discover Music
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
